/**
 * Sticky top header for the Check-In Session Detail page.
 *
 * Renders: back chevron, session number, customer line, lifecycle/payment/
 * intake chips, the job and invoice numbers as links, expiry countdown. On desktop, the primary action buttons
 * also live in this header (passed in as `desktopActions`). On mobile, the
 * actions live in the sticky bottom bar so this header is just identification.
 *
 * On-system styling (sm4rt-web-ui skill): recessive back control, shared Chip,
 * design-system type/colour tokens (see checkInSession.css).
 */

import React from 'react';
import { IconArrowLeft } from '@tabler/icons-react';
import type { CheckInSession } from '@techgitter/contracts';

import { Chip, toneFromMuiColor } from '../../chip';
import {
  intakeTypeLabel,
  lifecycleChipColor,
  lifecycleLabel,
  paymentChipColor,
  paymentChipRestatesLifecycle,
  paymentLabel,
  sessionInvoiceRef,
  sessionTicketRef,
  ticketGroupingLabel,
} from './sessionDetailHelpers';

export interface CheckInSessionHeaderProps {
  session: CheckInSession;
  onBack: () => void;
  /**
   * Desktop-only action slot. The page renders the bottom action bar
   * unconditionally; on desktop we ALSO pass actions in here for a
   * traditional top-right placement (CSS hides it below the md breakpoint).
   */
  desktopActions?: React.ReactNode;
}

/** Expiry timestamp → "19 May 2026, 8:48 am". */
const formatExpiry = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const CheckInSessionHeader: React.FC<CheckInSessionHeaderProps> = ({
  session,
  onBack,
  desktopActions,
}) => {
  const customerLine = [session.customerName, session.customerPhone, session.businessName]
    .filter(Boolean)
    .join(' · ');

  // Four lifecycle statuses are payment statuses, so these two chips said the
  // same thing twice ("Awaiting Payment" · "Awaiting payment"). The lifecycle
  // chip is the one that is always there, so it keeps the slot.
  const showPaymentChip = !paymentChipRestatesLifecycle(
    session.lifecycleStatus as string,
    session.paymentState as string,
  );

  // Why this check-in has one ticket, or several. Shown for every session
  // whose devices this app raised tickets for, including after the fact: a
  // technician looking at one ticket covering three laptops has no other way
  // to tell that it was chosen rather than defaulted.
  const showGroupingChip = session.intakeType !== 'express';

  // The job and invoice a person quotes, reachable without opening the Session
  // card. **Only when a display number exists** — the header is for references
  // someone can read out, and an RS internal id is not one. Until the number
  // lands (a fresh ticket, or the ~5 min invoice sync) the row simply omits it
  // and the Session card still carries the RepairShopr link.
  const ticket = sessionTicketRef(session);
  const invoice = sessionInvoiceRef(session);
  const refs: { key: string; label: string; value: string; href: string }[] = [];
  if (ticket?.sm4rtHref) {
    refs.push({ key: 'job', label: 'Job', value: `#${ticket.label}`, href: ticket.sm4rtHref });
  }
  if (invoice?.sm4rtHref) {
    refs.push({
      key: 'invoice', label: 'Invoice', value: invoice.label, href: invoice.sm4rtHref,
    });
  }

  const showExpiry =
    session.expiresAt && !['checked_in', 'cancelled'].includes(session.lifecycleStatus as string);

  return (
    <div className="checkin-sticky-top-header">
      <div className="ci-header ci-centre">
        <button type="button" className="ci-header__back" onClick={onBack} aria-label="Back to dashboard">
          <IconArrowLeft size={18} stroke={1.5} />
        </button>

        <div className="ci-header__main">
          <div className="ci-header__toprow">
            <span className="ci-header__number">{session.sessionNumber}</span>
            <Chip tone="neutral">{intakeTypeLabel(session.intakeType)}</Chip>
            {showGroupingChip && (
              <Chip tone="neutral">{ticketGroupingLabel(session.ticketGrouping)}</Chip>
            )}
            <Chip tone={toneFromMuiColor(lifecycleChipColor(session.lifecycleStatus))}>
              {lifecycleLabel(session.lifecycleStatus)}
            </Chip>
            {showPaymentChip && (
              <Chip tone={toneFromMuiColor(paymentChipColor(session.paymentState))}>
                {paymentLabel(session.paymentState)}
              </Chip>
            )}
            {session.requiresAttention && <Chip tone="error">Needs attention</Chip>}
          </div>

          {customerLine && <div className="ci-header__customer">{customerLine}</div>}

          {refs.length > 0 && (
            /* Links, not chips: a chip carries a tone and reads as a status,
               and these are references to other records — the same distinction
               the Session card's record trail makes. */
            <div className="ci-header__refs">
              {refs.map(ref => (
                <span className="ci-header__ref" key={ref.key}>
                  <span className="ci-header__ref-label">{ref.label}</span>
                  <a
                    className="ci-header__ref-link"
                    href={ref.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {ref.value}
                  </a>
                </span>
              ))}
            </div>
          )}

          {showExpiry && (
            <div className="ci-header__expiry">Expires {formatExpiry(session.expiresAt as string)}</div>
          )}
        </div>

        {desktopActions && <div className="ci-header__actions">{desktopActions}</div>}
      </div>
    </div>
  );
};
